import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type OverflowMenuItem = {
  id: string;
  label: string;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
};

type Props = {
  label: string;
  items: readonly OverflowMenuItem[];
  size?: 'sm' | 'md';
  align?: 'start' | 'end';
};

type Coords = {
  top: number;
  left: number;
};

const MENU_GAP_PX = 4;
const VIEWPORT_PAD_PX = 8;
const FALLBACK_MENU_WIDTH_PX = 192;
const FALLBACK_MENU_HEIGHT_PX = 80;

function computeCoords(
  trigger: DOMRect,
  menuWidth: number,
  menuHeight: number,
  align: 'start' | 'end',
): Coords {
  const fitsBelow =
    trigger.bottom + MENU_GAP_PX + menuHeight <= window.innerHeight - VIEWPORT_PAD_PX;
  const top = fitsBelow
    ? trigger.bottom + MENU_GAP_PX
    : Math.max(VIEWPORT_PAD_PX, trigger.top - MENU_GAP_PX - menuHeight);

  const preferredLeft = align === 'end' ? trigger.right - menuWidth : trigger.left;
  const maxLeft = window.innerWidth - VIEWPORT_PAD_PX - menuWidth;
  const left = Math.min(Math.max(VIEWPORT_PAD_PX, preferredLeft), Math.max(VIEWPORT_PAD_PX, maxLeft));
  return { top, left };
}

export function OverflowMenu({ label, items, size = 'sm', align = 'end' }: Props) {
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }

    const update = () => {
      const trigger = triggerRef.current;
      if (trigger === null) {
        return;
      }
      const triggerRect = trigger.getBoundingClientRect();
      if (
        triggerRect.bottom < 0 ||
        triggerRect.top > window.innerHeight ||
        triggerRect.right < 0 ||
        triggerRect.left > window.innerWidth
      ) {
        setOpen(false);
        return;
      }
      const menu = listRef.current;
      const menuWidth = menu?.offsetWidth ?? FALLBACK_MENU_WIDTH_PX;
      const menuHeight = menu?.offsetHeight ?? FALLBACK_MENU_HEIGHT_PX;
      setCoords(computeCoords(triggerRect, menuWidth, menuHeight, align));
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, align, items.length]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        (rootRef.current !== null && rootRef.current.contains(target)) ||
        (listRef.current !== null && listRef.current.contains(target))
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={`overflow-menu overflow-menu--${align}`} ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`button button--ghost button--${size} overflow-menu__trigger`}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open
        ? createPortal(
            <ul
              className="overflow-menu__list"
              id={menuId}
              role="menu"
              aria-label={label}
              ref={listRef}
              style={
                coords === null
                  ? { top: 0, left: 0, visibility: 'hidden' }
                  : { top: coords.top, left: coords.left }
              }
            >
              {items.map((item) => (
                <li key={item.id} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className={[
                      'overflow-menu__item',
                      item.danger === true ? 'overflow-menu__item--danger' : null,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    disabled={item.disabled === true}
                    onClick={() => {
                      setOpen(false);
                      item.onSelect();
                    }}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
